import { Component, computed, inject, input, signal } from '@angular/core'
import { DatePipe } from '@angular/common'
import { FormsModule } from '@angular/forms'
import { TranslateService } from '@ngx-translate/core'
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap'
import { LocalStorage } from 'ngx-store'

import { ElementsUserProfile, MCPSession, MCPUserKey } from 'elements-sdk'
import { APIService } from 'api/services/api.service'
import { MessageBoxService } from 'common/util/services/messageBox.service'
import { NotifyService } from 'common/util/services/notify.service'
import { TranslatePipe } from 'common/locale'
import { TranslateDirective } from 'common/locale/directives/translate.directive'
import { EntityListComponent } from 'common/entityList/components/entityList.component'
import { EntityListItemDirective } from 'common/entityList/components/entityListItems.component'
import { AutoselectDirective } from 'common/util/directives/autoselect.directive'
import { CopyToClipboardButtonDirective } from 'common/util/directives/copyToClipboardButton.directive'
import { EmptyStateComponent, MessageComponent } from 'common/util'
import { BadgeModule } from 'common/util.badge'
import { IdentityService } from 'main/services/identity.service'
import { ParametersService } from 'main/services/parameters.service'

@Component({
  selector: 'mcp-management',
  templateUrl: './mcpManagement.component.html',
  imports: [
    TranslateDirective,
    FormsModule,
    EntityListComponent,
    EntityListItemDirective,
    AutoselectDirective,
    CopyToClipboardButtonDirective,
    TranslatePipe,
    DatePipe,
    NgbTooltip,
    MessageComponent,
    BadgeModule,
    EmptyStateComponent,
  ],
})
export class McpManagementComponent {
  user = input.required<ElementsUserProfile>()
  mcpKey = signal<MCPUserKey | null>(null)
  mcpSessions = signal<MCPSession[]>([])
  urlPrefix = computed(() => {
    const url = this.parameters.values.externalUrl
    const prefix = `${location.protocol}//${location.host}`
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    return url || prefix
  })

  #api = inject(APIService)
  #notify = inject(NotifyService)
  #translate = inject(TranslateService)
  #messageBox = inject(MessageBoxService)
  protected readonly parameters = inject(ParametersService)
  protected readonly identity = inject(IdentityService)

  @LocalStorage('mcpDisclaimerDismissed') disclaimerDismissed = false

  constructor () {
    if (this.parameters.values.mcpTunnelEnabled) {
      this.#api.main.getMyMcpKey().then(key => {
        this.mcpKey.set(key)
      })
      this.#api.main.getMyMcpSessions().then(sessions => {
        this.mcpSessions.set(sessions)
      })
    }
  }

  async activate () {
    try {
      await this.#messageBox.show({
        title: this.#translate.instant('AI agents disclaimer'),
        text: this.#translate.instant('AI agents will be able to control the ELEMENTS system on your behalf. Make sure that you understand the actions being taken and their consequences. The ELEMENTS support will not be able to assist you with issues caused by AI agents.'),
        positive: this.#translate.instant('I understand'),
        negative: this.#translate.instant('Cancel'),
        danger: true,
      })
    } catch { return }
    this.disclaimerDismissed = true
  }

  async rotateMcpKey () {
    this.mcpKey.set(await this.#api.main.rotateMyMcpKey())
    this.#notify.success(this.#translate.instant('MCP connection code regenerated'))
  }

  async revokeMcpSession (session: MCPSession) {
    this.mcpSessions.update(s => s.filter(s => s !== session))
    await this.#api.main.revokeMyMcpSession({ sessionId: String(session.id) })
  }
}
